import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";

const STRINGS = {
  it: {
    title: "Concierge R.P.",
    sub: "Risponde subito, 24/7",
    placeholder: "Chiedi prezzi, bagagli, servizi…",
    welcome: "Buongiorno! Sono il concierge di R.P. Limousine Milano. Come posso aiutarla?",
    error: "Ops, qualcosa è andato storto. Riprova o scrivici su WhatsApp.",
  },
  en: {
    title: "R.P. Concierge",
    sub: "Instant answers, 24/7",
    placeholder: "Ask about prices, luggage, services…",
    welcome: "Good day! I'm the R.P. Limousine Milano concierge. How may I help you?",
    error: "Oops, something went wrong. Please retry or message us on WhatsApp.",
  },
};

export default function ChatWidget() {
  const { lang } = useContext(AppContext);
  const s = STRINGS[lang];
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionRef = useRef(
    localStorage.getItem("rpl_chat_session") ||
      (() => {
        const id = `s_${Math.random().toString(36).slice(2, 12)}`;
        localStorage.setItem("rpl_chat_session", id);
        return id;
      })()
  );

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: s.welcome }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text, lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let failed = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) failed = true;
            if (parsed.t) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.t };
                return copy;
              });
            }
          } catch { /* partial chunk */ }
        }
      }
      if (failed) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: s.error };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: s.error };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen(!open)}
        aria-label="Chat concierge"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#D4AF37] shadow-[0_8px_30px_rgba(212,175,55,0.4)]"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.94 }}
      >
        {open ? <X className="h-6 w-6 text-[#0B0C0E]" /> : <Sparkles className="h-6 w-6 text-[#0B0C0E]" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-4 z-50 flex h-[480px] w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#131519] shadow-2xl sm:right-6"
          >
            <div className="border-b border-[rgba(212,175,55,0.15)] px-5 py-4">
              <p className="font-display text-lg font-semibold text-white">{s.title}</p>
              <p className="flex items-center gap-1.5 text-[11px] text-neutral-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {s.sub}
              </p>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#D4AF37] text-[#0B0C0E]"
                        : "bg-[#0B0C0E] text-neutral-200"
                    }`}
                  >
                    {m.content || <Loader2 className="h-4 w-4 animate-spin text-[#D4AF37]" />}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
            <div className="flex gap-2 border-t border-[rgba(212,175,55,0.15)] p-3">
              <input
                data-testid="chat-input"
                className="input-dark !py-2.5"
                placeholder={s.placeholder}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
              />
              <button
                data-testid="chat-send-btn"
                onClick={send}
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#D4AF37] text-[#0B0C0E] transition-colors hover:bg-[#F3E08F] disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
